import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';

import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { useSbkEntryHeader } from '@/feature/bets-sbk/components/bet-cards/use-sbk-entry-header';
import { Bet } from '@/feature/bets-sbk/hooks/types';
import { classifyBetVariant } from '@/feature/bets-sbk/utils/classify-bet-variant';
import { useShareEntryCapture } from '@/feature/entry-share/hooks/use-share-entry-capture';
import { Share } from '@/feature/entry-share/utils/share';
import { useIsPredictionsProduct } from '@/hooks/use-is-predictions-product';
import { useStableCallback } from '@/hooks/use-stable-callback';
import { logger } from '@/utils/logging';

import { createSbkShareLink } from '../utils/create-share-link';

const LOG_TAG = '[SBK EntryShare]';

export const useSbkBetShare = (viewRef: React.RefObject<View | null>, bet: Bet) => {
    const { t } = useTranslation('pickem_share_entry_modal');
    const { showToast } = useAlerts();
    const { captureShareImage, presentShareLink, removeShareScreen } = useShareEntryCapture(viewRef);
    const { title, subtitle } = useSbkEntryHeader(bet, classifyBetVariant(bet));
    const isPredictions = useIsPredictionsProduct();
    const linkType = isPredictions ? 'predictions' : 'sbk';

    const capture = async () => {
        try {
            const screenshot = await captureShareImage();

            const shareImageOnly = async () => {
                try {
                    await Share.open({ url: screenshot, failOnCancel: false });
                } catch (e) {
                    logger.warn(LOG_TAG, 'Share sheet failed', e);
                } finally {
                    removeShareScreen();
                }
            };

            try {
                presentShareLink(screenshot, await createSbkShareLink(bet, { title, desc: subtitle }, linkType));
            } catch (e) {
                logger.warn(LOG_TAG, 'Could not create share link, sharing image only', e);
                await shareImageOnly();
            }
        } catch (e) {
            logger.debug(LOG_TAG, e);
            showToast({ message: t('sharing_failed_msg'), toastType: 'warning' });
            removeShareScreen();
        }
    };

    const startCapture = useStableCallback(capture);

    // 0.5 seconds delay to allow the view to render before capturing the screenshot
    useEffect(() => {
        const timer = setTimeout(startCapture, 500);
        return () => clearTimeout(timer);
    }, [startCapture]);
};
